import { useCallback, useEffect, useRef, useState } from "react";

export const COPY_FEEDBACK_MS = 1500;

export type CopyStatus = "idle" | "copying" | "success" | "error";

export type CopyTextSource =
  | string
  | (() => string | null | undefined | Promise<string | null | undefined>);

interface UseCopyToClipboardOptions {
  onSuccess?: () => void;
  onError?: (error: unknown) => void;
}

export function useCopyToClipboard({
  onSuccess,
  onError,
}: UseCopyToClipboardOptions = {}) {
  const [status, setStatus] = useState<CopyStatus>("idle");
  const resetTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearResetTimeout = useCallback(() => {
    if (resetTimeoutRef.current) {
      clearTimeout(resetTimeoutRef.current);
      resetTimeoutRef.current = null;
    }
  }, []);

  useEffect(() => () => clearResetTimeout(), [clearResetTimeout]);

  useEffect(() => {
    if (status === "success" || status === "error") {
      clearResetTimeout();
      resetTimeoutRef.current = setTimeout(() => {
        setStatus("idle");
      }, COPY_FEEDBACK_MS);
    }
  }, [status, clearResetTimeout]);

  const copy = useCallback(
    async (source: CopyTextSource) => {
      if (status === "copying") return;

      clearResetTimeout();
      setStatus("copying");

      try {
        if (!navigator?.clipboard?.writeText) {
          throw new Error("Clipboard API is not available");
        }

        const text = typeof source === "function" ? await source() : source;
        if (!text) {
          throw new Error("No text to copy");
        }

        await navigator.clipboard.writeText(text);
        setStatus("success");
        onSuccess?.();
      } catch (error) {
        console.error("Failed to copy to clipboard", error);
        setStatus("error");
        onError?.(error);
      }
    },
    [clearResetTimeout, onError, onSuccess, status]
  );

  return { status, isProcessing: status === "copying", copy };
}
